import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "@/types/profile";
import { Heart, X, MessageCircle, Circle } from "lucide-react";

interface UserIdentityCardProps {
  profile: Profile;
  onLike: () => void;
  onPass: () => void;
  onMessage?: () => void;
}

const UserIdentityCard = memo(({
  profile,
  onLike,
  onPass,
  onMessage,
}: UserIdentityCardProps) => {
  const { t } = useTranslation();
  
  // Get display name: username if available, otherwise first name
  const getDisplayName = () => {
    if (profile.username) return profile.username;
    if (profile.name) return profile.name.split(' ')[0];
    return 'User';
  };

  // Format last seen time
  const formatLastSeen = useMemo(() => {
    if (!profile.lastSeen) return null;
    
    const lastSeenDate = new Date(profile.lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - lastSeenDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Consider online if last seen within last 5 minutes
    if (diffMins < 5) {
      return { text: t("popBalloon.online"), isOnline: true };
    }

    if (diffMins < 60) {
      const unit = diffMins === 1 ? t("popBalloon.minute") : t("popBalloon.minutes");
      return { text: `${diffMins} ${unit} ${t("popBalloon.ago")}`, isOnline: false };
    }
    
    if (diffHours < 24) {
      const unit = diffHours === 1 ? t("popBalloon.hour") : t("popBalloon.hours");
      return { text: `${diffHours} ${unit} ${t("popBalloon.ago")}`, isOnline: false };
    }
    
    if (diffDays < 7) {
      const unit = diffDays === 1 ? t("popBalloon.day") : t("popBalloon.days");
      return { text: `${diffDays} ${unit} ${t("popBalloon.ago")}`, isOnline: false };
    }
    
    return { text: lastSeenDate.toLocaleDateString(), isOnline: false };
  }, [profile.lastSeen, t]);

  // Format location
  const formatLocation = () => {
    if (profile.distance) {
      // Check if distance is "Distance unavailable" or similar
      const distanceLower = profile.distance.toLowerCase();
      if (distanceLower.includes("unavailable") || distanceLower.includes("not available")) {
        return t("popBalloon.distanceUnavailable");
      }
      return profile.distance;
    }
    if ((profile as any).location) {
      return (profile as any).location;
    }
    return t("popBalloon.distanceUnavailable");
  };

  return (
    <div className="bg-card rounded-2xl p-4 shadow-lg border border-border/50 mx-4">
      <div className="flex items-center justify-between">
        {/* Left side - User info */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-bold text-foreground truncate">
              {getDisplayName()}
            </h3>
            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <span className="text-xs text-muted-foreground">👤</span>
            </div>
          </div>
          
          {/* Age and Location */}
          <p className="text-sm text-muted-foreground mb-1">
            {profile.age} • {formatLocation()}
          </p>
          
          {/* Last active time */}
          <div className="flex items-center gap-1.5">
            {formatLastSeen?.isOnline ? (
              <>
                <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                <span className="text-xs text-muted-foreground">{t("popBalloon.online")}</span>
              </>
            ) : formatLastSeen ? (
              <span className="text-xs text-muted-foreground">
                {formatLastSeen.text}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">{t("popBalloon.offline")}</span>
            )}
          </div>
        </div>

        {/* Right side - Interaction icons */}
        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          {/* Like/Interested */}
          <button
            onClick={onLike}
            className="p-2 rounded-full bg-teal-500/10 hover:bg-teal-500/20 transition-colors group"
            aria-label={t("popBalloon.like")}
          >
            <Heart className="w-5 h-5 text-teal-500 fill-teal-500 group-hover:scale-110 transition-transform" />
          </button>
          
          {/* Pass/X */}
          <button
            onClick={onPass}
            className="p-2 rounded-full bg-gray-200/50 hover:bg-gray-300/50 transition-colors group"
            aria-label={t("popBalloon.pass")}
          >
            <X className="w-5 h-5 text-gray-600 group-hover:scale-110 transition-transform" />
          </button>
          
          {/* Message (optional) */}
          {onMessage && (
            <button
              onClick={onMessage}
              className="p-2 rounded-full bg-gray-200/50 hover:bg-gray-300/50 transition-colors group"
              aria-label={t("popBalloon.message")}
            >
              <MessageCircle className="w-5 h-5 text-gray-600 group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

UserIdentityCard.displayName = "UserIdentityCard";

export default UserIdentityCard;

